"use client";

import type { BasketItem, BasketStore } from "@/stores/basket-store";

// #region Type Definitions

/**
 * Defines the shape of actions for the optimistic basket reducer.
 * Uses a discriminated union for type safety.
 */
export type OptimisticBasketAction =
  | {
      type: "add";
      payload: Omit<BasketItem, "quantity"> & { quantity?: number };
    }
  | {
      type: "remove";
      payload: { id: string; variantId?: string };
    }
  | {
      type: "update";
      payload: { id: string; quantity: number; variantId?: string };
    }
  | {
      type: "clear";
    };

// #endregion

// #region Utility Functions

/**
 * Creates a unique identifier for a basket item based on its ID and optional variant ID.
 * This ensures that the same product with different variants is treated as a distinct item.
 * @param id - The base product ID.
 * @param variantId - The optional variant ID.
 * @returns A unique string key for the basket item.
 */
export const createBasketItemKey = (id: string, variantId?: string): string => {
  return variantId ? `${id}-${variantId}` : id;
};

/**
 * Recalculates the total count and price for a given set of basket items.
 * This utility centralizes basket state calculations to ensure consistency.
 * @param items - An array of basket items.
 * @returns An object containing the total item count and the total price.
 */
export const calculateBasketTotals = (
  items: BasketItem[]
): { count: number; total: number } => {
  return items.reduce(
    (acc, item) => {
      acc.count += item.quantity;
      acc.total += item.price * item.quantity;
      return acc;
    },
    { count: 0, total: 0 }
  );
};

// #endregion

// #region Optimistic Reducer

/**
 * A reducer function for use with React 19's `useOptimistic` hook.
 * It handles basket state changes optimistically based on dispatched actions.
 *
 * @param state - The current basket state from the store.
 * @param action - The dispatched action with a specific type and payload.
 * @returns The new, optimistically updated state.
 */
export const optimisticBasketReducer = (
  state: BasketStore,
  action: OptimisticBasketAction
): BasketStore => {
  switch (action.type) {
    case "add": {
      const newItem = action.payload;
      const itemKey = createBasketItemKey(newItem.id, newItem.variant?.id);
      const quantity = newItem.quantity || 1;

      const existingItem = state.items.find(
        (i) => createBasketItemKey(i.id, i.variant?.id) === itemKey
      );

      const newItems = existingItem
        ? state.items.map((i) =>
            createBasketItemKey(i.id, i.variant?.id) === itemKey
              ? { ...i, quantity: i.quantity + quantity }
              : i
          )
        : [...state.items, { ...newItem, quantity }];

      return { ...state, items: newItems, ...calculateBasketTotals(newItems) };
    }

    case "remove": {
      const { id, variantId } = action.payload;
      const itemKey = createBasketItemKey(id, variantId);

      const newItems = state.items.filter(
        (i) => createBasketItemKey(i.id, i.variant?.id) !== itemKey
      );

      return { ...state, items: newItems, ...calculateBasketTotals(newItems) };
    }

    case "update": {
      const { id, quantity, variantId } = action.payload;
      const itemKey = createBasketItemKey(id, variantId);

      const newItems = state.items
        .map((i) =>
          createBasketItemKey(i.id, i.variant?.id) === itemKey
            ? { ...i, quantity: Math.max(0, quantity) }
            : i
        )
        .filter((i) => i.quantity > 0); // Remove item if quantity is 0

      return { ...state, items: newItems, ...calculateBasketTotals(newItems) };
    }

    case "clear": {
      return { ...state, items: [], count: 0, total: 0 };
    }

    default:
      return state;
  }
};

// #endregion
